import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import { useChartTheme } from '../BarChart';
import styles from '../BarChart/Charts.module.scss';

/* ────────────────────────────────────────────────────────────
 * Sparkline — compact trendline for StatCards & inline contexts.
 *
 *   <Sparkline values={[2, 4, 3, 6, 5, 9, 7]} accent="success" />
 *   <Sparkline values={[...]} type="area" />
 *
 * No axes, no legend, no tooltip — sparklines are glanceable
 * trend cues, not analytic surfaces. For an analytic surface
 * use <LineChart>.
 * ──────────────────────────────────────────────────────────── */

const ACCENT_TO_TOKEN = {
  primary: 'var(--color-primary)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
};

const Sparkline = ({ values, accent, color, type, height, strokeWidth, className, ariaLabel }) => {
  const palette = useChartTheme();
  const data = useMemo(() => values.map((v, i) => ({ i, v })), [values]);
  const resolvedColor = color || ACCENT_TO_TOKEN[accent] || palette.series[0];

  return (
    <span
      className={[styles.sparkline, className || ''].filter(Boolean).join(' ')}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        {type === 'area' ? (
          <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <Area
              type="monotone"
              dataKey="v"
              stroke={resolvedColor}
              fill={resolvedColor}
              fillOpacity={0.18}
              strokeWidth={strokeWidth}
              isAnimationActive
              animationDuration={400}
            />
          </AreaChart>
        ) : (
          <LineChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <Line
              type="monotone"
              dataKey="v"
              stroke={resolvedColor}
              strokeWidth={strokeWidth}
              dot={false}
              isAnimationActive
              animationDuration={400}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </span>
  );
};

Sparkline.propTypes = {
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
  accent: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
  color: PropTypes.string,
  type: PropTypes.oneOf(['line', 'area']),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  strokeWidth: PropTypes.number,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Sparkline.defaultProps = {
  accent: 'primary',
  color: '',
  type: 'line',
  height: 32,
  strokeWidth: 2,
  className: '',
  ariaLabel: 'Trend sparkline',
};

export default Sparkline;
